import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import BoltIcon from '@mui/icons-material/Bolt';
import RedeemIcon from '@mui/icons-material/Redeem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import firebase from '../../../utilities/firebase';
import { StateManager } from '../../../utilities/stateManager';
import {
  DEFAULT_CONCIERGE_FEE,
  financeLineItems,
  saveFinanceInvoice,
} from '../../../utilities/financeInvoice';
import FinanceInvoiceModal from './FinanceInvoiceModal';
import AddIncentiveModal from './AddIncentiveModal';

const USD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

export default function FinanceInvoicePanel({
  invoices = [],
  deal = {},
  stockNumber,
  disabled,
  defaultAccount = '',
  onInvoicesChanged,
  onDepositAdded,
}) {
  const [localInvoices, setLocalInvoices] = React.useState(invoices);
  const [activeId, setActiveId] = React.useState(deal.finance);
  const [invoiceModalOpen, setInvoiceModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [incentiveOpen, setIncentiveOpen] = React.useState(false);
  const [showOthers, setShowOthers] = React.useState(false);
  const [quickSaving, setQuickSaving] = React.useState(false);

  React.useEffect(() => { setLocalInvoices(invoices); }, [invoices]);
  React.useEffect(() => { setActiveId(deal.finance); }, [deal.finance]);

  const activeInvoice = localInvoices.find(inv => inv.id === activeId) || null;
  const otherInvoices = localInvoices.filter(inv => inv.id !== activeId);
  const lineItems = activeInvoice ? financeLineItems(activeInvoice) : [];

  // Keep this panel and the parent tab in sync after any write.
  const commit = (nextInvoices, nextActiveId) => {
    setLocalInvoices(nextInvoices);
    setActiveId(nextActiveId);
    onInvoicesChanged?.(nextInvoices, nextActiveId);
  };

  const upsert = (invoice) => {
    const exists = localInvoices.some(inv => inv.id === invoice.id);
    const next = exists
      ? localInvoices.map(inv => inv.id === invoice.id ? invoice : inv)
      : [...localInvoices, invoice];
    commit(next, invoice.id);
  };

  // The overwhelmingly common case: a plain $995 concierge-fee invoice. One click.
  const quickInvoice = async () => {
    setQuickSaving(true);
    try {
      const saved = await saveFinanceInvoice({
        stockNumber,
        invoice: { conciergeFee: DEFAULT_CONCIERGE_FEE },
      });
      upsert(saved);
      StateManager.setAlertAndOpen(`Finance invoice created for ${USD(DEFAULT_CONCIERGE_FEE)}`, 'success');
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen('Failed to create the invoice', 'error');
    }
    setQuickSaving(false);
  };

  const selectInvoice = async (inv) => {
    await firebase.firestore().doc('deals/' + stockNumber).set({ finance: inv.id }, { merge: true });
    commit(localInvoices, inv.id);
  };

  const deleteInvoice = async (invoiceId, e) => {
    e?.stopPropagation();
    if (!StateManager.isAdmin()) return;
    if (!window.confirm('Delete this finance invoice?')) return;
    await firebase.firestore().collection('invoices').doc(invoiceId).delete();
    const next = localInvoices.filter(inv => inv.id !== invoiceId);
    if (invoiceId === activeId) {
      await firebase.firestore().doc('deals/' + stockNumber).set({ finance: null }, { merge: true });
      commit(next, null);
    } else {
      commit(next, activeId);
    }
  };

  return (
    <Box>
      {activeInvoice ? (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
            <Typography variant="overline" sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700 }}>
              Active Invoice
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.25 }}>
              {!disabled && (
                <IconButton size="small" title="Edit invoice" onClick={() => { setEditing(activeInvoice); setInvoiceModalOpen(true); }}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              {StateManager.isAdmin() && (
                <IconButton size="small" color="error" title="Delete invoice" onClick={(e) => deleteInvoice(activeInvoice.id, e)}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          <Box sx={{ bgcolor: 'grey.50', borderRadius: 1.5, p: 1.75 }}>
            {lineItems.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>No line items</Typography>
            )}
            {lineItems.map((item) => (
              <Box key={item.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.35, gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.label}
                  </Typography>
                  {item.isIncentive && (
                    <Chip label="incentive" size="small" variant="outlined" color="info" sx={{ height: 16, fontSize: 9, flexShrink: 0 }} />
                  )}
                </Box>
                <Typography variant="body2" sx={{ flexShrink: 0 }}>{USD(item.amount)}</Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{USD(activeInvoice.total)}</Typography>
            </Box>
          </Box>

          {!disabled && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" startIcon={<RedeemIcon />} onClick={() => setIncentiveOpen(true)}>
                Add Incentive
              </Button>
              <Button size="small" startIcon={<AddIcon />} onClick={() => { setEditing(null); setInvoiceModalOpen(true); }}>
                New Invoice
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            No finance invoice yet
          </Typography>
          {!disabled && (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<BoltIcon />}
                onClick={quickInvoice}
                disabled={quickSaving}
              >
                {quickSaving ? 'Creating…' : `Quick Invoice · ${USD(DEFAULT_CONCIERGE_FEE)}`}
              </Button>
              <Button variant="outlined" size="small" onClick={() => { setEditing(null); setInvoiceModalOpen(true); }}>
                Custom
              </Button>
            </Box>
          )}
        </Box>
      )}

      {otherInvoices.length > 0 && (
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Box
            onClick={() => setShowOthers(!showOthers)}
            sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {otherInvoices.length} other invoice{otherInvoices.length > 1 ? 's' : ''}
            </Typography>
            {showOthers
              ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </Box>
          <Collapse in={showOthers}>
            {otherInvoices.map(inv => (
              <Box
                key={inv.id}
                sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{USD(inv.total)}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{inv.date || '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button size="small" variant="outlined" onClick={() => selectInvoice(inv)} disabled={disabled}>
                    Select
                  </Button>
                  {StateManager.isAdmin() && (
                    <IconButton size="small" color="error" onClick={(e) => deleteInvoice(inv.id, e)}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
          </Collapse>
        </Box>
      )}

      <FinanceInvoiceModal
        open={invoiceModalOpen}
        onClose={() => { setInvoiceModalOpen(false); setEditing(null); }}
        stockNumber={stockNumber}
        invoice={editing}
        onSaved={(saved) => upsert(saved)}
      />

      <AddIncentiveModal
        open={incentiveOpen}
        onClose={() => setIncentiveOpen(false)}
        stockNumber={stockNumber}
        invoice={activeInvoice}
        defaultAccount={defaultAccount}
        onApplied={({ invoice, deposit }) => {
          upsert(invoice);
          onDepositAdded?.(deposit);
        }}
      />
    </Box>
  );
}
